/**
 * Record side panel — the full attribute list for one record, and the place
 * new records are created.
 *
 * The table is for scanning; this is for working on a single record. It groups
 * attributes by who owns them, so it is obvious at a glance which half of the
 * record you can change and which belongs to the source system.
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Trash2, Lock, Sparkles, ChevronsUpDown } from 'lucide-react';
import type { Attribute, Override } from './EditableCell';
import { ReferencePicker, type Option } from './ReferencePicker';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  object: string;
  objectLabel: string;
  mode: 'editable' | 'overlay' | 'readonly';
  sourceSystem: string | null;
  attributes: Attribute[];
  /** null puts the panel in create mode. */
  record: (Record<string, unknown> & { __key: string; __overrides?: Record<string, Override> | null }) | null;
  /** Labels for the reference values on this record, keyed by target object. */
  lookups?: Record<string, Option[]>;
  onSaved: () => void;
}

function personName(email: string | null): string {
  if (!email) return 'Unknown';
  return email.split('@')[0].split(/[._-]/).filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * A reference is stored as a key but chosen by name. Without this the panel
 * asked for a UUID to be typed by hand — which is how a bank account gets
 * attached to the wrong entity.
 */
function ReferenceField({
  attr, value, options, disabled, onChange,
}: {
  attr: Attribute;
  value: string;
  options: Option[];
  disabled: boolean;
  onChange: (v: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const label = options.find((o) => o.value === value)?.label ?? value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="h-8 w-full justify-between px-2 text-xs font-normal"
        >
          <span className={cn('truncate', !label && 'text-muted-foreground')}>{label || '—'}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <ReferencePicker
          object={attr.references_object!}
          seed={options}
          current={value || null}
          allowClear={!attr.is_required}
          onPick={(v) => { onChange(v); setOpen(false); }}
          onCancel={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

export function RecordSheet({
  open, onOpenChange, object, objectLabel, mode, sourceSystem, attributes, record, lookups, onSaved,
}: Props) {
  const isNew = record === null;
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { setDraft({}); }, [record, open]);

  const editable = attributes.filter((a) => a.ownership === 'user');
  const sourced = attributes.filter((a) => a.ownership === 'source');
  const derived = attributes.filter((a) => a.ownership === 'derived');

  // Creating a row means writing the table directly, so source columns are
  // fair game — nothing upstream owns the row yet.
  const creatable = attributes.filter((a) => a.ownership !== 'derived');

  const effective = (a: Attribute): unknown => {
    if (a.api_slug in draft) return draft[a.api_slug];
    const ov = record?.__overrides?.[a.api_slug];
    return ov?.value ?? record?.[a.api_slug] ?? '';
  };

  const save = async () => {
    if (!Object.keys(draft).length) { onOpenChange(false); return; }
    setBusy(true);
    try {
      const res = await fetch(`/api/fin/records/${object}`, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(isNew ? { values: draft } : { key: record!.__key, values: draft }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(body.error ?? 'Could not save', {
          description: body.rejected?.join(', ') ?? body.hint,
        });
        return;
      }
      toast.success(isNew ? `${objectLabel} created` : 'Saved');
      onSaved();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  const destroy = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/fin/records/${object}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ key: record!.__key }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // The API distinguishes "referenced by other records" from a generic
        // failure — pass that through, it tells the user what to do next.
        toast.error(body.error ?? 'Could not delete', { description: body.hint });
        return;
      }
      toast.success('Deleted');
      onSaved();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  /** Labels known for a reference attribute's target object. */
  const optionsFor = (a: Attribute): Option[] =>
    (a.references_object && lookups?.[a.references_object]) || [];

  /** A reference cell shows the name; everything else shows the stored value. */
  const shown = (a: Attribute, v: unknown): string => {
    if (v === null || v === undefined || v === '') return '';
    if (a.type !== 'reference') return String(v);
    return optionsFor(a).find((o) => o.value === String(v))?.label ?? String(v);
  };

  const field = (a: Attribute) => {
    const v = effective(a);
    const set = (nv: unknown) => setDraft((d) => ({ ...d, [a.api_slug]: nv }));

    if (a.type === 'reference' && a.references_object) {
      return (
        <ReferenceField
          attr={a}
          value={String(v ?? '')}
          options={optionsFor(a)}
          disabled={busy}
          onChange={set}
        />
      );
    }
    if (a.type === 'boolean') {
      return (
        <Switch checked={Boolean(v)} onCheckedChange={set} disabled={busy} />
      );
    }
    if (a.type === 'select' && a.options) {
      return (
        <Select value={String(v ?? '')} onValueChange={set} disabled={busy}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            {a.options.map((o) => (
              <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        value={String(v ?? '')}
        onChange={(e) => set(e.target.value === '' ? null : e.target.value)}
        disabled={busy}
        type={a.type === 'number' || a.type === 'currency' ? 'number' : a.type === 'date' ? 'date' : 'text'}
        className="h-8 text-xs"
      />
    );
  };

  const readOnlyRow = (a: Attribute) => {
    const ov = record?.__overrides?.[a.api_slug];
    const src = record?.[a.api_slug];
    const srcText = shown(a, src);
    return (
      <div key={a.api_slug} className="grid grid-cols-[130px_1fr] items-start gap-3 py-1.5">
        <span className="text-xs text-muted-foreground pt-0.5">{a.label}</span>
        <span className="text-xs font-mono break-words">
          {srcText === '' ? (
            <span className="text-muted-foreground/50 italic">empty</span>
          ) : srcText}
          {ov?.value != null && String(ov.value) !== String(src) && (
            <span className="block mt-0.5 text-[11px] text-amber-600 dark:text-amber-500">
              {personName(ov.by)} set “{shown(a, ov.value)}”
            </span>
          )}
        </span>
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col gap-0 p-0">
        <SheetHeader className="px-5 py-4">
          <SheetTitle className="text-base">
            {isNew ? `New ${objectLabel.replace(/s$/, '')}` : objectLabel}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {isNew
              ? 'This record is created in the application — nothing upstream owns it.'
              : mode === 'overlay'
                ? `Values come from ${sourceSystem ?? 'the warehouse'}. You can change the classification below; it is stored separately and survives each import.`
                : 'This record belongs to the application.'}
          </SheetDescription>
        </SheetHeader>
        <Separator />

        <ScrollArea className="flex-1">
          <div className="px-5 py-4 space-y-5">
            {isNew ? (
              <div className="space-y-3">
                {creatable.map((a) => (
                  <div key={a.api_slug} className="space-y-1">
                    <Label className="text-xs">
                      {a.label}
                      {a.is_required && <span className="text-destructive ml-0.5">*</span>}
                    </Label>
                    {field(a)}
                  </div>
                ))}
              </div>
            ) : (
              <>
                {editable.length > 0 && (
                  <section className="space-y-3">
                    <div className="flex items-center gap-2">
                      <h4 className="text-[11px] font-semibold uppercase tracking-wider">Yours to set</h4>
                      <Badge variant="secondary" className="h-4 px-1 text-[9px]">{editable.length}</Badge>
                    </div>
                    {editable.map((a) => (
                      <div key={a.api_slug} className="space-y-1">
                        <Label className="text-xs">{a.label}</Label>
                        {field(a)}
                        {record?.__overrides?.[a.api_slug]?.by && (
                          <p className="text-[11px] text-muted-foreground">
                            Last set by {personName(record.__overrides[a.api_slug].by)}
                          </p>
                        )}
                      </div>
                    ))}
                  </section>
                )}

                {sourced.length > 0 && (
                  <section>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Lock className="h-3 w-3 text-muted-foreground" />
                      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        From {sourceSystem ?? 'the warehouse'}
                      </h4>
                    </div>
                    <div className="divide-y">{sourced.map(readOnlyRow)}</div>
                  </section>
                )}

                {derived.length > 0 && (
                  <section>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Sparkles className="h-3 w-3 text-muted-foreground" />
                      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Calculated
                      </h4>
                    </div>
                    <div className="divide-y">{derived.map(readOnlyRow)}</div>
                  </section>
                )}
              </>
            )}
          </div>
        </ScrollArea>

        <Separator />
        <SheetFooter className="flex-row items-center gap-2 px-5 py-3">
          {!isNew && mode === 'editable' && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={busy}>
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this record?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The row is removed along with any classification attached to it. A full copy is
                    kept in the change log, so this can be reconstructed — but not undone from here.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={destroy}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={busy || (!isNew && !Object.keys(draft).length)}>
            {isNew ? 'Create' : 'Save'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
